import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { bool, func, shape } from "prop-types";
import * as i18n from "util/i18n";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import { useLocation, useParams } from "react-router-dom";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import { Col, Row } from "react-bootstrap";
import Button from "pages/_components/Button";
import { TransactionCard, TransactionHeader, TransactionSign } from "pages/_components/TransactionCard";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import gaEventTracker, { events } from "util/gaEventTracker";
import { isEmpty } from "lodash";
import CustomData from "pages/_components/CustomData/CustomData";
import CustomText from "pages/_components/CustomText/CustomText";
import { push } from "connected-react-router";
import { ResponsiveHeader } from "pages/_components/header";

const SuccessTransactionPage = ({ dispatch, fetching, transaction, isDesktop, newAccountTicket }) => {
    const { id } = useParams();
    const { pathname, state = {} } = useLocation();
    const isLoading = fetching || !transaction?.data;
    const isBackoffice = pathname.includes("backoffice");
    const backLinkTo = newAccountTicket?.backLinkTo || null;
    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        "accounts.create.transactionRequest.secondTitle",
        id,
        "accounts",
    );

    useEffect(() => {
        if (id && isEmpty(state?.transaction)) {
            dispatch(formActions.readTransactionTicket(id));
        }
    }, [id, dispatch]);

    useEffect(
        () => () => {
            dispatch(accountsActions.cleanNewAccountTicket());
        },
        [],
    );

    const [showMoreData, setShowMoreData] = useState(!isDesktop);

    const handleDownload = async () => {
        gaEventTracker.triggerEvent(events.cuentas_solicitud_descargar());
        await setShowMoreData(true);
        return handlePrint();
    };

    return (
        <Fragment>
            <Notification scopeToShow="accounts" />{" "}
            {!isBackoffice && (
                <ResponsiveHeader 
                    ticket 
                    metaTitle={i18n.get("", "accounts.create.transactionRequest.secondTitle")} 
                    backLinkTo={backLinkTo}/>
            )}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader
                                title="accounts.create.transactionRequest.secondTitle"
                                shorterStatusTitle
                            />
                            <div className="ticket" style={{ backgroundColor: "unset" }}>
                                <div className="ticket__wrapper" style={{ backgroundColor: "#fff" }}>
                                    <div className="ticket__header" />
                                    <div className="ticket__body echeq-massive-emit">
                                        <div
                                            className="grid-table-data flex"
                                            style={{
                                                gridArea: "1 / 1 / span 1 / span 8",
                                                justifyContent: "center",
                                                flexDirection: "row",
                                                textAlign: "center",
                                            }}>
                                            <div
                                                className={`ticket-section ${isDesktop && " flex"}`}
                                                style={{ alignItems: "center" }}>
                                                <CustomData
                                                    keepSpaceIfNotitle
                                                    titleTextType="I18N"
                                                    titleText="accounts.create.accountType"
                                                    valueText={`accounts.create.accountType.${transaction?.data?.account?.productType}`}
                                                    valueTextType="I18N"
                                                />
                                                <div
                                                    className="custom-data"
                                                    style={!isDesktop ? { paddingTop: "10px" } : {}}>
                                                    <CustomText
                                                        text={i18n.get("accounts.create.modalities", "Modalidad")}
                                                        className="custom-data-title"
                                                    />
                                                    <span className="custom-text custom-data-value">
                                                        {transaction?.data?.modalities?.map((modality, index) => (
                                                            <>
                                                                {!!index && ", "}
                                                                {i18n.get(`accounts.create.modality.${modality}`)}
                                                            </>
                                                        ))}
                                                        {!!transaction?.data?.modalityReason?.length && (
                                                            <>
                                                                {!!transaction?.data?.modalities?.length && ", "}
                                                                {transaction?.data?.modalityReason}
                                                            </>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="view-more-data-button" id="element-to-ignore">
                                            <div className="ticket__divider inside-ticket__body element-to-ignore">
                                                <div className="ticket__notch ticket__notch--left element-to-ignore" />
                                                <div className="ticket__notch ticket__notch--right element-to-ignore" />
                                            </div>
                                            <Button
                                                aria-expanded={showMoreData ? "true" : "false"}
                                                className="more-button no-print"
                                                label={
                                                    showMoreData ? "echecks.seeLess.button" : "echecks.seeMore.button"
                                                }
                                                bsStyle="link"
                                                onClick={() => setShowMoreData((v) => !v)}
                                                image={
                                                    showMoreData
                                                        ? "figma/icon/arrow-up.svg"
                                                        : "figma/icon/arrow-down.svg"
                                                }
                                            />
                                        </div>
                                        <div className={`view-more-data ${showMoreData ? "expand " : "collapse"}`}>
                                            <div className="aditional-data">
                                                <h2>
                                                    {i18n.get("creditBill.aditionalData.label", "Datos adicionales")}
                                                </h2>
                                                <CustomData
                                                    titleText="accounts.list.export.accountNumber"
                                                    titleTextType="I18N"
                                                    valueText={transaction?.data?.number}
                                                    valueTextType="TEXT"
                                                />
                                                <CustomData
                                                    titleText="accounts.details.information.uniformBankCode.label"
                                                    titleTextType="I18N"
                                                    valueText={transaction?.data?.cbu}
                                                    valueTextType="TEXT"
                                                />
                                                <CustomData
                                                    titleText="accounts.create.periodicity"
                                                    titleTextType="I18N"
                                                    valueText={`accounts.create.periodicity.${transaction?.data?.periodicity}`}
                                                    valueTextType="I18N"
                                                />
                                                <CustomData
                                                    titleText="accounts.create.purpose"
                                                    titleTextType="I18N"
                                                    valueText={
                                                        transaction?.data?.purpose === "OTHER"
                                                            ? transaction?.data?.purposeReason
                                                            : i18n.get(
                                                                  `accounts.create.purpose.${transaction?.data?.purpose}`,
                                                              )
                                                    }
                                                />
                                                <CustomData
                                                    titleText="accounts.create.messageToBank"
                                                    titleTextType="I18N"
                                                    valueText={transaction?.data?.messageToBank || '""'}
                                                />
                                            </div>
                                            <TransactionSign
                                                showMoreData={showMoreData}
                                                customText="pending.approval.cybo"
                                                isDesktop={isDesktop}
                                                creationTime
                                                isSmall
                                                hasVaucher
                                            />
                                        </div>
                                    </div>
                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                                {!isBackoffice && (
                                    <Row className="form-actions element-to-ignore no-print">
                                        {isDesktop && (
                                            <Col md={6} xs={12}>
                                                <Button
                                                    className="btn btn-outline"
                                                    label="forms.transaction.ticket.actions.backToHome"
                                                    onClick={() => dispatch(push("/desktop"))}
                                                />
                                            </Col>
                                        )}
                                        <Col xs={12} md={6}>
                                            <Button
                                                style={!isDesktop ? { marginTop: "15px" } : {}}
                                                onClick={handleDownload}
                                                label="global.download"
                                                className="btn btn-primary element-to-ignore"
                                                image="images/download.svg"
                                            />
                                        </Col>
                                        {!isDesktop && (
                                            <Col xs={12} md={6}>
                                                <Button
                                                    className="btn btn-outline element-to-ignore"
                                                    image="images/figma/icon/share.svg"
                                                    label="global.share"
                                                    onClick={() => {
                                                        handleShareMobile();
                                                    }}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                )}
                            </div>
                        </TransactionCard>
                    </div>
                </div>
            </MainContainer>
        </Fragment>
    );
};

SuccessTransactionPage.propTypes = {
    transaction: shape({}).isRequired,
    dispatch: func.isRequired,
    fetching: bool,
    isDesktop: bool.isRequired,
};

SuccessTransactionPage.defaultProps = {
    fetching: false,
};

const mapStateToProps = (state) => ({
    transaction: formSelectors.getTransaction(state),
    fetching: accountsSelectors.getFetching(state),
    newAccountTicket: accountsSelectors.getNewAccountTicket(state),
});

export default connect(mapStateToProps)(SuccessTransactionPage);
